"use client";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

export interface FetchEmailsDetail {
  phase: "fetching" | "processing" | "done" | "idle";
  total: number;
  fetched: number;
  processed: number;
  emailsStored: number;
}

export interface FetchEmailsState {
  running: boolean;
  progress: number;
  status: string | null;
  detail: FetchEmailsDetail | null;
  startFetch: () => Promise<void>;
  dismiss: () => void;
}

const FetchEmailsContext = createContext<FetchEmailsState>({
  running: false,
  progress: 0,
  status: null,
  detail: null,
  startFetch: async () => {},
  dismiss: () => {},
});

export function FetchEmailsProvider({ children }: { children: React.ReactNode }) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const [detail, setDetail] = useState<FetchEmailsDetail | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applyStatus = useCallback((d: Record<string, unknown>) => {
    const prog = Number(d.progress ?? 0);
    setProgress(prog);
    if (d.error) {
      setStatus(String(d.error));
      setDetail(null);
      setRunning(false);
      return "stop";
    }
    if (!d.running) {
      setProgress(100);
      setStatus(`Done — ${d.emails_stored ?? 0} emails stored across ${d.total ?? 0} threads`);
      setDetail({
        phase: "done",
        total: Number(d.total ?? 0),
        fetched: Number(d.fetched ?? 0),
        processed: Number(d.processed ?? 0),
        emailsStored: Number(d.emails_stored ?? 0),
      });
      setRunning(false);
      return "stop";
    }
    setRunning(true);
    setStatus(null);
    setDetail({
      phase: (d.phase as FetchEmailsDetail["phase"]) ?? "fetching",
      total: Number(d.total ?? 0),
      fetched: Number(d.fetched ?? 0),
      processed: Number(d.processed ?? 0),
      emailsStored: Number(d.emails_stored ?? 0),
    });
    return "continue";
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const d = await fetch("/email/api/fetch-emails").then((r) => r.json());
        const result = applyStatus(d);
        if (result === "stop") stopPolling();
      } catch { /* ignore */ }
    }, 1500);
  }, [stopPolling, applyStatus]);

  // On mount: check if a fetch is already running and resume progress bar
  useEffect(() => {
    fetch("/email/api/fetch-emails")
      .then((r) => r.json())
      .then((d) => {
        if (d.running) {
          applyStatus(d);
          startPolling();
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startFetch = useCallback(async () => {
    setRunning(true);
    setProgress(2);
    setStatus("Starting…");
    setDetail({ phase: "fetching", total: 0, fetched: 0, processed: 0, emailsStored: 0 });
    await fetch("/email/api/fetch-emails", { method: "POST" });
    startPolling();
  }, [startPolling]);

  const dismiss = useCallback(() => {
    setStatus(null);
    setDetail(null);
    setProgress(0);
    setRunning(false);
  }, []);

  return (
    <FetchEmailsContext.Provider value={{ running, progress, status, detail, startFetch, dismiss }}>
      {children}
    </FetchEmailsContext.Provider>
  );
}

export function useFetchEmails() {
  return useContext(FetchEmailsContext);
}
